import { ReadonlyArray } from "@morphism/fp";
import { Tooltip as AntTooltip } from "antd";
import React from "react";
import styled from "styled-components";

import { NewBranch as GenericBranch } from "~/Branch";
import { Button } from "~/ButtonV2";
import { Company } from "~/Company";
import { Driver } from "~/Driver";
import * as Log from "~/Log";
import { Order } from "~/Order";
import { Select as GenericSelect } from "~/SelectV2";

export namespace Branch {
  export const column = ({
    branchAndLocationNames,
  }: {
    readonly branchAndLocationNames: readonly string[];
  }) => {
    const filterDropdown = (props: Filter.Props) => (
      <Filter branchAndLocationNames={branchAndLocationNames} {...props} />
    );

    return {
      title: "Branch / Location",
      dataIndex: "branch",
      filterDropdown,
      onFilter,
      sorter,
      render,
    };
  };

  export const Name = (driver: Driver) => {
    const branchName = GenericBranch.name(driver.branch);
    const driverHomeOffice = driver.homeOffice;
    return (
      <div data-testid="log-branch-name-tooltip-wrapper">
        {driver.branch?.name ? (
          <AntTooltip
            title={GenericBranch.TooltipTitle("Driver's branch:", branchName)}
            mouseEnterDelay={0.5}
          >
            {branchName}
          </AntTooltip>
        ) : driverHomeOffice ? (
          <Company.Location
            {...driverHomeOffice}
            labelPrefix="Driver's work reporting location:"
          />
        ) : (
          <>{branchName}</>
        )}
      </div>
    );
  };

  export const render = (_: unknown, { summary }: Log.Summary) => {
    return <Name {...summary.driver} />;
  };

  export const order = () =>
    Order.fromCompare<string | undefined>((a, b) =>
      Order.string().compare((a ?? "").toLowerCase(), (b ?? "").toLowerCase())
    );

  export const { compare } = order();

  const sorter = (a: Log.Summary, b: Log.Summary) => {
    const aValue = a.summary.driver.branch?.name
      ? a.summary.driver.branch?.name.toLowerCase()
      : a.summary.driver.homeOffice
      ? Company.Location.toString(a.summary.driver.homeOffice).toLowerCase()
      : undefined;
    const bValue = b.summary.driver.branch?.name
      ? b.summary.driver.branch?.name.toLowerCase()
      : b.summary.driver.homeOffice
      ? Company.Location.toString(b.summary.driver.homeOffice).toLowerCase()
      : undefined;
    return Order.optionally(compare)(aValue, bValue);
  };

  export type BranchAndLocationNames = {
    readonly branchAndLocationNames: readonly string[];
  };

  export const uniqueBranchAndLocationNames = ({ summaries }: Log.Summaries) =>
    pipe(
      summaries.reduce<readonly string[]>((reduction, { summary }) => {
        const branchName = summary.driver.branch?.name;
        if (branchName) {
          if (!reduction.some((reducedName) => reducedName === branchName)) {
            return [...reduction, branchName];
          }
        } else {
          const locationName = summary.driver?.homeOffice
            ? Company.Location.toString(summary.driver?.homeOffice)
            : undefined;
          if (locationName) {
            if (
              !reduction.some((reducedName) => reducedName === locationName)
            ) {
              return [...reduction, locationName];
            }
          }
        }
        return reduction;
      }, []),
      ReadonlyArray.sort(Order.string())
    );

  const onFilter = (
    value: string | number | boolean,
    { summary: { driver } }: Log.Summary
  ) =>
    driver.branch?.name
      ? driver.branch.name === value
      : driver.homeOffice &&
        Company.Location.toString(driver.homeOffice) === `${value}`;

  function Filter({
    branchAndLocationNames,
    clearFilters,
    confirm,
    selectedKeys,
    setSelectedKeys,
  }: Filter.Props & BranchAndLocationNames) {
    return (
      <>
        <Filter.Controls>
          <Button
            type="link"
            size="small"
            disabled={!selectedKeys[0]}
            onClick={clearFilters}
          >
            Reset
          </Button>
          <Button type="primary" size="small" onClick={confirm}>
            OK
          </Button>
        </Filter.Controls>
        <Filter.Tags>
          <Filter.Select
            branchAndLocationNames={branchAndLocationNames}
            selectedKeys={selectedKeys}
            setSelectedKeys={setSelectedKeys}
          />
        </Filter.Tags>
      </>
    );
  }

  namespace Filter {
    export type Props = {
      readonly confirm: () => void;
      readonly clearFilters?: () => void;
      readonly selectedKeys: readonly string[];
      readonly setSelectedKeys: (keys: readonly string[]) => void;
    };

    export const Controls = styled.div`
      display: flex;
      justify-content: space-between;
      padding: 8px;
      margin-bottom: 4px;
      border-bottom: 1px solid #f0f0f0;
    `;

    export const Tags = styled.div`
      padding: 0 4px 4px 4px;
    `;

    export const Select = ({
      branchAndLocationNames,
      selectedKeys,
      setSelectedKeys,
    }: Pick<Props, "selectedKeys" | "setSelectedKeys"> &
      BranchAndLocationNames) => {
      const filterOptions = branchAndLocationNames.map((name) => ({
        key: name,
        value: name,
      }));

      return (
        <GenericSelect
          loading={branchAndLocationNames.length < 1}
          placeholder="Filter by branch or location"
          mode="multiple"
          bordered={false}
          onChange={
            ((_value: never, selected: readonly { readonly key: string }[]) =>
              setSelectedKeys(selected.map(({ key }) => key))) as never
          }
          options={filterOptions}
          selectedKeys={selectedKeys}
          optionToKey={({ key }) => key}
          optionToValue={({ value }) => value}
          Option={({ value }) => <>{value}</>}
        />
      );
    };
  }
}
